<template>
  <div class="py-4">
    <div class="row">
      <div class="col">
        <div class="subtitle">
          <h2>Notes</h2>
          <p>
            Leave your comment or remark to help your teammates to understand
            the task
          </p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-auto">
        <user-avatar :avatarURL="currentUser.avatar_src"></user-avatar>
      </div>
      <div class="col task-advanced-comment">
        <ckeditor-wfh
          :editorConfigs="editorConfigs"
          v-model="comment"
          @submit="submitComment"
          ref="editor"
          @input="emitInputData"
        ></ckeditor-wfh>
      </div>
    </div>

    <div class="mt-5">
      <div class="mb-4" v-for="comment of comments">
        <div class="row" style="flex-wrap: nowrap">
          <div class="col-auto">
            <user-avatar :avatarURL="comment.owner.avatar_src"></user-avatar>
          </div>
          <div class="col">
            <div class="row">
              <div class="col-auto wfh-comment-name">
                {{ displayName(comment.owner) }}
              </div>
              <div class="col wfh-comment-date pl-0">
                {{ postingTime(comment.created_at) }}
              </div>
            </div>

            <div class="row mb-1">
              <div class="col">
                <div class="wfh-comment-content" v-html="comment.content"></div>
              </div>
            </div>

            <div class="row" v-if="currentUserIsOwner(comment)">
              <div class="col">
                <button
                  type="button"
                  class="btn wfh-btn-link-secondary p-0"
                  style="color: #808fa4; font-size: 12px"
                  @click="removeComment(comment)"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Mention from "@ckeditor/ckeditor5-mention/src/mention";
import { getUsers } from "@/services/UserService";
import {
  mentionCustomization,
  mentionItemRendererWithAvatar
} from "@/services/CKEditorService";
import _ from "lodash";

export default {
  props: {
    taskId: {
      required: false
    }
  },

  data() {
    return {
      comment: "",
      comments: [],
      editorConfigs: {
        placeholder: "Newline: Alt+Enter, Submit: Enter",

        plugins: [Mention, mentionCustomization, this.keydownHandler],

        mention: {
          feeds: [
            {
              marker: "@",
              feed: getUsers,
              itemRenderer: mentionItemRendererWithAvatar
            }
          ]
        }
      }
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    }
  },

  methods: {
    async fetchComments() {
      const res = await axios.get(`/tasks/${this.taskId}/comments`);

      this.comments = res.data.data;
    },

    async submitComment() {
      const parsedElement = new DOMParser().parseFromString(
        this.comment,
        "text/html"
      );
      const elem = parsedElement.body.childNodes[0];
      const spanElements = elem.querySelectorAll("span");
      let mentionedIds = [];

      for (const spanElement of spanElements) {
        mentionedIds.push(spanElement.dataset.mentionedUserId);
      }

      if (!this.taskId) {
        this.comments.unshift({
          key: this.comments.length,
          content: this.comment,
          owner: this.currentUser,
          mentionedIds
        });
        this.$emit("changeComments", this.comments);
        this.resetComment();

        return;
      }
      setTimeout(async () => {
        const res = await axios.post(`/tasks/${this.taskId}/comments`, {
          content: this.comment,
          mentionedIds
        });

        this.comments = await _.orderBy(
          res.data.data.comments,
          ["id"],
          ["desc"]
        );
        this.resetComment();
      }, 400);
    },

    async removeComment(comment) {
      if (typeof comment.key !== "undefined") {
        const newComments = this.comments.slice();

        this.comments = newComments.filter(newComment => {
          return newComment.key !== comment.key;
        });
        this.$emit("changeComments", this.comments);

        return;
      }

      await axios.delete("/comments", {
        params: {
          comment_ids: [comment.id_hash]
        }
      });
      const newComments = this.comments.slice();

      this.comments = newComments.filter(newComment => {
        return newComment.id_hash !== comment.id_hash;
      });
    },

    displayName(user) {
      return user.display_name !== null ? user.display_name : user.name;
    },

    postingTime(dateTime) {
      const createdAt = moment(dateTime).format("YYYY-MM-DD HH:mm:ss");
      const currentTime = moment();

      if (currentTime.isSame(createdAt, "day")) {
        return moment(createdAt).format("h:mm a");
      }

      if (currentTime.diff(createdAt, "days") > 1) {
        return moment(createdAt).format("MMMM Do YYYY, h:mm a");
      }

      return moment(createdAt).format("[Yesterday at] h:mm a");
    },

    currentUserIsOwner(comment) {
      return this.currentUser.id === comment.owner.id;
    },

    resetComment() {
      this.comment = "";
    },

    keydownHandler(editor) {
      editor.keystrokes.set("Enter", (keyEvtData, cancel) => {
        cancel();

        const event = new Event("submitTaskComment");
        document.dispatchEvent(event);
      });
    },

    emitInputData() {
      this.$emit("input", this.comment);
    }
  },

  mounted() {
    if (this.taskId) {
      this.fetchComments();
    }

    document.addEventListener("submitTaskComment", this.submitComment);
  }
};
</script>

<style scoped></style>
